import React, { useRef, useState } from "react";
import { Alert, Box, Button, Grid, useTheme } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  ItemGrid,
  MainStyledTypography,
  StyledTypography,
} from "../../../components/layouts/SubHeader/SubHeaderStyled";
import { getSubHeaderBannerBackground } from "../../../components/layouts/SubHeader/subHeaderBannerStyles";
import useIsMobile from "../../../hooks/useIsMobile";
import DeleteSearchDialog from "../DeleteSearchDialog";
import FilterMenu from "./FilterMenu";
import SavedSearchPickerPanel from "./SavedSearchPickerPanel";
import { useFilterControllerContext } from "./FilterControllerContext";

export const MODIFIED_SAVED_SEARCH_TITLE = "New Search";

/**
 * V3/V4 page banner: "Search" + dynamic name (all companies / saved search) + chevron picker.
 * When applied filters diverge from a loaded saved search, title becomes "New Search".
 * V4 row actions are pin + rename/delete submenu.
 * While rename mode is active, only the static "Search" title is shown.
 * Mobile: compact height + smaller type so filters/results appear earlier.
 */
export default function V3SearchSubHeader() {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const controller = useFilterControllerContext();
  const titleRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);

  if (!controller) {
    return null;
  }

  const {
    panelContext,
    clearAll,
    deleteDialogOpen,
    setDeleteDialogOpen,
    itemToDelete,
    setItemToDelete,
    confirmDelete,
    isDeleting,
    isSavedSearchDirty,
    isEditingSavedSearch,
    savedSearchLoad,
    retrySavedSearch,
  } = controller;

  const selectedName = panelContext.selectedSavedSearch?.name
    ? String(panelContext.selectedSavedSearch.name).trim()
    : "";
  const isModifiedFromSaved = Boolean(isSavedSearchDirty) && !isEditingSavedSearch;
  const dynamicLabel = isModifiedFromSaved
    ? MODIFIED_SAVED_SEARCH_TITLE
    : selectedName || "all companies";
  const textPrimary = theme.palette.text.primary;
  const ctaPurple = theme.palette.primary.main;
  const rowActions = panelContext.rowActions || "delete";

  const openMenu = () => setMenuOpen(true);
  const closeMenu = () => setMenuOpen(false);

  const handleSelect = (selected) => {
    if (!selected) {
      panelContext.onSelectSavedSearch?.(null, null);
      clearAll({ searchAllCompanies: true });
      closeMenu();
      return;
    }
    panelContext.onSelectSavedSearch?.(null, selected);
    closeMenu();
  };

  const titleAriaLabel = isModifiedFromSaved
    ? `Current search ${MODIFIED_SAVED_SEARCH_TITLE}. Choose a saved search`
    : selectedName
      ? `Current search ${selectedName}. Choose a saved search`
      : "Search all companies. Choose a saved search";

  const titleFontSize = isMobile ? 16 : 24;
  const titleLineHeight = isMobile ? "22px" : "30px";
  const bannerMinHeight = isMobile ? 52 : 94;
  const bannerPadding = isMobile ? "10px 16px 10px 16px" : "19px 120px 19px 48px";

  return (
    <>
      <Grid
        container
        position="relative"
        alignItems="center"
        sx={{
          background: getSubHeaderBannerBackground(theme),
          minHeight: bannerMinHeight,
        }}
        data-testid="v3-search-subheader"
      >
        <ItemGrid data-compact={isMobile ? "true" : undefined}>
          <Grid
            container
            flexDirection="column"
            justifyContent="center"
            style={{ padding: bannerPadding }}
          >
            <Grid item>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 0.5,
                  maxWidth: "100%",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                <StyledTypography
                  component="span"
                  sx={{
                    color: textPrimary,
                    fontSize: titleFontSize,
                    lineHeight: titleLineHeight,
                    fontWeight: isMobile ? 700 : 800,
                  }}
                >
                  Search
                </StyledTypography>

                {isEditingSavedSearch ? null : (
                  <Button
                    ref={titleRef}
                    type="button"
                    onClick={openMenu}
                    aria-haspopup="dialog"
                    aria-expanded={menuOpen ? "true" : "false"}
                    aria-controls="v3-saved-search-menu"
                    data-testid="v3-saved-search-title-trigger"
                    aria-label={titleAriaLabel}
                    endIcon={
                      <ExpandMoreIcon
                        sx={{
                          fontSize: isMobile ? 18 : 22,
                          color: ctaPurple,
                          transform: menuOpen ? "rotate(180deg)" : "none",
                          transition: "transform 160ms ease",
                        }}
                      />
                    }
                    sx={{
                      textTransform: "none",
                      minWidth: 0,
                      minHeight: isMobile ? 36 : undefined,
                      px: 0.5,
                      py: 0,
                      borderRadius: 1,
                      color: ctaPurple,
                      backgroundColor: "transparent",
                      border: "none",
                      boxShadow: "none",
                      verticalAlign: "baseline",
                      "&:hover": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.06)"
                            : "rgba(74, 44, 245, 0.06)",
                        border: "none",
                        boxShadow: "none",
                      },
                      "& .MuiButton-endIcon": {
                        marginLeft: 0.25,
                        marginRight: 0,
                      },
                    }}
                  >
                    <MainStyledTypography
                      component="span"
                      sx={{
                        color: ctaPurple,
                        fontSize: titleFontSize,
                        lineHeight: titleLineHeight,
                        fontWeight: isMobile ? 700 : 800,
                        maxWidth: { xs: 180, sm: 420, md: 560 },
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block",
                      }}
                    >
                      {dynamicLabel}
                    </MainStyledTypography>
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </ItemGrid>
      </Grid>

      {savedSearchLoad === "loading" && (
        <Box role="status" sx={{ px: 2, py: 1 }}>Loading saved search…</Box>
      )}
      {savedSearchLoad === "error" && (
        <Alert severity="error" action={<>
          <Button color="inherit" onClick={retrySavedSearch}>Retry</Button>
          <Button color="inherit" onClick={() => clearAll({ searchAllCompanies: true })}>All companies</Button>
        </>}>
          Couldn't load this saved search. Try again.
        </Alert>
      )}

      {isEditingSavedSearch ? null : (
        <FilterMenu
          id="v3-saved-search-menu"
          open={menuOpen}
          anchorEl={titleRef.current}
          onClose={closeMenu}
          width={360}
        >
          <SavedSearchPickerPanel
            savedSearchChoices={panelContext.savedSearchChoices}
            selectedSavedSearch={panelContext.selectedSavedSearch}
            onSelect={handleSelect}
            onDeleteSavedSearch={(event, option) => {
              panelContext.onDeleteSavedSearch?.(event, option);
            }}
            onPinSavedSearch={panelContext.onPinSavedSearch}
            onRenameSavedSearch={panelContext.onRenameSavedSearch}
            pinnedIds={panelContext.pinnedIds}
            rowActions={rowActions}
            onRequestClosePicker={closeMenu}
          />
        </FilterMenu>
      )}

      <DeleteSearchDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={confirmDelete}
        searchName={itemToDelete?.name}
        isDeleting={isDeleting}
      />
    </>
  );
}
